import { Stack } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Fragment, lazy, type JSX } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router';
import { ForbiddenError, ForbiddenFallback } from './components/Fallback/GenericErrorFallback';
import { CollectionOnlyForbiddenMessage } from './constants/collection-only-messages';
import { ROUTES } from './constants/RouteNameMapping';
import { useIsCollectionOnly } from './hooks/useIsCollectionOnly';
import { KPIRequestStatus } from './data-models/kpi-requests.data-model';
import { BulkUploadReportLoader } from './pages/AdminPanel/BulkUploadReport/BulkUploadReport';
import { UploadStatus } from './pages/AdminPanel/BulkUploadReport/UploadStatus';
import { ConfigColumns } from './pages/Config/Columns/ConfigColumns';
import { ConfigFormatters } from './pages/Config/Formatters/ConfigFormatters';
import { CustomFields } from './pages/AdminPanel/CustomFields/CustomFields';
import { FieldManager } from './pages/AdminPanel/FieldManager/FieldManager';
import { KpiSourcesConfig } from './pages/AdminPanel/KpiSources/KpiSourcesConfig';
import { useKpiSourceHierarchyAvailable } from './pages/AdminPanel/KpiSources/useKpiSourceHierarchyAvailable';
import { AuditorDateRangesConfig } from './pages/AdminPanel/AuditorDateRanges/AuditorDateRangesConfig';
import { RoundCreationConfig } from './pages/AdminPanel/RoundCreation/RoundCreationConfig';
import { DataCollectionConfig } from './pages/AdminPanel/DataCollectionConfig/DataCollectionConfig';
import { CompanyDetailsConfig } from './pages/AdminPanel/DataDisplay/CompanyDetailsConfig';
import { CompanyListsOverviewConfig } from './pages/AdminPanel/DataDisplay/CompanyListsOverviewConfig';
import { CompanySummaryFinancialSettingsConfig } from './pages/AdminPanel/DataDisplay/CompanySummaryFinancialSettingsConfig';
import { CompanySummaryKPIChartsConfig } from './pages/AdminPanel/DataDisplay/CompanySummaryKPIChartsConfig';
import { CompanySummaryPerformanceSettingsConfig } from './pages/AdminPanel/DataDisplay/CompanySummaryPerformanceSettingsConfig';
import { DataDisplay } from './pages/AdminPanel/DataDisplay/DataDisplay';
import { InvestmentMetricsConfig } from './pages/AdminPanel/DataDisplay/InvestmentMetricsConfig';
import { KpiDisplayConfig } from './pages/AdminPanel/DataDisplay/KpiDisplayConfig';
import { ListCustomization } from './pages/AdminPanel/DataDisplay/ListCustomization';
import { ProfileCustomization } from './pages/AdminPanel/DataDisplay/ProfileCustomization';
import {
  TearSheetCapitalizationSummaryConfig,
  TearSheetCompanyDescriptionConfig,
  TearSheetCompanySummaryConfig,
  TearSheetConfig,
  TearSheetFinancingHistoryConfig,
  TearSheetHeaderConfig,
  TearSheetInvestmentSummaryConfig,
  TearSheetInvestmentThesisConfig,
  TearSheetPerformanceCommentaryConfig,
  TearSheetPerformanceSummaryConfig,
} from './pages/AdminPanel/DataDisplay/TearSheetConfig';
import { TearsheetV2LayoutBuilder } from './pages/AdminPanel/DataDisplay/TearsheetV2/TearsheetV2LayoutBuilder';
import { GeneralLedgerLoader } from './pages/AdminPanel/GeneralLedger/GeneralLedger';
import { BulkDealDocsUploadLoader } from './pages/AdminPanel/BulkDealDocsUpload/BulkDealDocsUpload';
import { UpdateDealDocsRoundLoader } from './pages/AdminPanel/UpdateDealDocsRound/UpdateDealDocsRound';
import { TearsheetGeneration } from './pages/AdminPanel/ManageAccess/BulkTearsheetGeneration/TearsheetGeneration';
import { ManageAccess } from './pages/AdminPanel/ManageAccess/ManageAccess';
import { ManageAccessColumnGridDisplayDataLoader } from './pages/AdminPanel/ManageAccess/ManageAccessColumnGridDisplay';
import { ManageAccessCompanyDataLoader } from './pages/AdminPanel/ManageAccess/ManageAccessCompany';
import { CompanyMappings } from './pages/AdminPanel/Mappings/CompanyMappings';
import { OrgProfile } from './pages/AdminPanel/OrgProfile/OrgProfile';
import { ResetReingestion } from './pages/AdminPanel/ResetReingestion/ResetReingestion';
import { RubricsConfigLoader } from './pages/AdminPanel/Rubrics/RubricsConfig';
import { TableDataDownloadLoader } from './pages/AdminPanel/TableDataDownload/TableDataDownload';
import { CaptableFormWrapper } from './pages/CompanyProfiles/CapTable/CapTableForm';
import { CompanyProfileCapTable } from './pages/CompanyProfiles/CapTable/CompanyProfileCapTable';
import { Companies, CompanyListLoader } from './pages/CompanyProfiles/Companies/Companies';
import { CompanyProfileFinancials } from './pages/CompanyProfiles/components/Financials/CompanyProfileFinancials';
import { DealsTab } from './pages/CompanyProfiles2/Deals/DealsTab';
import { DealsTabPreview } from './pages/CompanyProfiles2/Deals/DealsTabPreview';
import { CompanyProfilesPeople } from './pages/CompanyProfiles/People/CompanyProfilesPeople';
import { CompanyProfilePerformance } from './pages/CompanyProfiles/Performance/CompanyProfilePerformance';
import { ScenariosWrapper } from './pages/CompanyProfiles/Scenarios/AllScenarios/ScenariosWrapper';
import { ScenarioForm } from './pages/CompanyProfiles/Scenarios/SingleScenario/form/ScenarioForm';
import { SingleScenario } from './pages/CompanyProfiles/Scenarios/SingleScenario/SingleScenario';
import { ScenarioVisualization } from './pages/CompanyProfiles/Scenarios/SingleScenario/visualization/ScenarioVisualization';
import { WeightedOutcomeVisualization } from './pages/CompanyProfiles/Scenarios/SingleScenario/visualization/WeightedOutcomeVisualization';
import { CompaniesOverview } from './pages/CompanyProfiles2/CompaniesOverview';
import { CompanySummary2 } from './pages/CompanyProfiles2/Summary/CompanySummary2';
import { ConfigClient } from './pages/Config/Client/ConfigClient';
import { ConfigDealStage } from './pages/Config/ConfigDealStage/ConfigDealStage';
import { ConfigDealType } from './pages/Config/ConfigDealType/ConfigDealType';
import { FieldAutomationConfig } from './pages/Config/FieldAutomation/FieldAutomationConfig';
import { ConfigFund } from './pages/Config/Fund/ConfigFund';
import { ConfigKPIs } from './pages/Config/KPI/ConfigKPI';
import { ConfigKPIGroups } from './pages/Config/KPIGroups/ConfigKPIGroups';
import { ConfigMissReasons } from './pages/Config/Reasons/ConfigMissReasons';
import { ConfigPassReasons } from './pages/Config/Reasons/ConfigPassReasons';
import { ConfigSector } from './pages/Config/Sector/ConfigSector';
import { DealAnalytics } from './pages/DealFlow2/components/DealAnalytics/DealAnalytics';
import { DealAnalyticsDuration } from './pages/DealFlow2/components/DealAnalytics/Details/DealAnalyticsDuration';
import { DAOverview } from './pages/DealFlow2/components/DealAnalytics/Overview/DAOverview';
import { DealsView } from './pages/DealFlow2/DealFlow';
import { AllSecurities } from './pages/Finance2/AllSecurities/AllSecurities';
import { AllTransactions } from './pages/Finance2/AllTransactions/AllTransactions';
import { ReconSection } from './pages/Finance2/Dashboard/Recon/ReconSection';
import { RoundReconGrid } from './pages/Finance2/Dashboard/Recon/RoundReconGrid';
import { TransactionReconGrid } from './pages/Finance2/Dashboard/Recon/TransactionReconGrid';
import { FinanceRounds } from './pages/Finance2/Rounds/FinanceRounds';
import { LedgerGrid } from './pages/Finance2/Security/LedgerGrid';
import { TransactionHistory } from './pages/Finance2/Transactions/TransactionHistory';
import { TransactionsTable3 } from './pages/Finance2/Transactions3/Transactions3';
import { FPAllocationLoader } from './pages/FundProfiles/Allocation/FPAllocation';
import { FundReservesLoader } from './pages/FundProfiles/Reserves/FundReserves';
import { FPSummary } from './pages/FundProfiles/Summary/FPSummary';
import { FPWaterfall } from './pages/FundProfiles/Waterfall/FPWaterfall';
import { AffinityConfiguration } from './pages/Integrations/Affinity/AffinityConfiguration';
import { AirtableSync } from './pages/Integrations/Airtable/AirtableSync';
import { AllVueConfiguration } from './pages/Integrations/AllVue/AllVueConfiguration';
import { ArkPesConfiguration } from './pages/Integrations/ArkPes/ArkPesConfiguration';
import { IntegrationDetail } from './pages/Integrations/IntegrationDetail';
import { ILevelConfiguration } from './pages/Integrations/ILevel/ILevelConfiguration';
import { QuickBooksConfiguration } from './pages/Integrations/QuickBooks/QuickBooksConfiguration';
import { StandardMetricsConfiguration } from './pages/Integrations/StandardMetrics/StandardMetricsConfiguration';
import { CobaltConfiguration } from './pages/Integrations/Cobalt/CobaltConfiguration';
import { AllCompanies } from './pages/KPI/components/KPICompanies/AllCompanies';
import { AssignedCompanies } from './pages/KPI/components/KPICompanies/AssignedCompanies';
import { KPIRecipientView3 } from './pages/KPI/components/KPIRequests/KPIRequestResponse/KPIRecipientView3';
import { KPIRequestResponseHome } from './pages/KPI/components/KPIRequests/KPIRequestResponse/KPIRequestResponseHome';
import { KPIReviewerView } from './pages/KPI/components/KPIRequests/KPIRequestResponse/KPIReviewerView';
import { KPIThankYouScreen } from './pages/KPI/components/KPIRequests/KPIRequestResponse/KPIThankYouScreen';
import { ManagedServiceView } from './pages/KPI/components/KPIRequests/KPIRequestResponse/ManagedServiceView';
import { KPIResponses } from './pages/KPI/components/KPIRequests/KPIRequestsAndResponses';
import { KPIResponsesGrid } from './pages/KPI/components/KPIRequests/KPIResponsesGrid';
import { KpiRequestManageLoader } from './pages/KPI/components/KPIRequests/Manage/KpiRequestManage';
import { ResponseAnalysis } from './pages/KPI/components/KPIRequests/ResponseAnalysis/ResponseAnalysis';
import { SentRequests } from './pages/KPI/components/KPIRequests/SentRequests';
import { UnsentKpiRequestsGrid } from './pages/KPI/components/KPIRequests/UnsentKpiRequestsGrid';
import { KpiModuleGate } from './pages/KPI/KpiModuleGate';
import { PipelineModulePreview } from './pages/DealFlow2/PipelineModulePreview';
import { SourcingModulePreview } from './pages/Sourcing/SourcingModulePreview';
import { FrostedGate } from './components/FrostedGate/FrostedGate';
import { KPITemplate, KPITemplatesRedirect } from './pages/KPI/KPITemplate';
import { LoginSuccess } from './pages/LoginSuccess/LoginSuccess';
import { TempMprWrapper, getShowSeparateMprRoute } from './pages/TempMpr/TempMprWrapper';
import { UI } from './pages/UI/UI';
import { RouteWrapper } from './RouteWrapper';
import { PermissionKey, PersonaKey } from './services/PermissionAndRolesKeys';
import { PermissionService } from './services/PermissionService';
import { useBeneficialOwnershipEnabled } from './pages/EntityManagement/hooks/useBeneficialOwnershipEnabled';
import { useEntityManagementEnabled } from './pages/EntityManagement/hooks/useEntityManagementEnabled';
import { clientConfigFieldsAtom } from './services/state/AppConfigStateJ';
import { isNotProduction } from './util/environment';
import { MaggieFeatureFlags } from './util/feature-flags';
import { isMobileDevice } from './util/mobile';
import {
  KpiCollectionConfigsPage,
  KpiCollectionConfigsRedirect,
} from './pages/KPI/KpiCollection/KpiCollectionConfigsPage';
import {
  CustomKpiTemplatePage,
  CustomKpiTemplateRedirect,
} from './pages/KPI/KpiCollection/CustomKpiTemplate/CustomKpiTemplatePage';
import { KpiCompanySettings } from './pages/KPI/KpiCollection/KpiCompanySettings';
import { CompanyPeriodRequestManageLoader } from './pages/KPI/ManageRequests/CompanyPeriodRequestManageLoader';
import { CompanyPeriodRequestManageLoaderV2 } from './pages/KPI/ManageRequests/CompanyPeriodRequestManageLoaderV2';
import { TearsheetV1LayoutBuilder } from './pages/AdminPanel/DataDisplay/TearsheetV1/TearsheetV1LayoutBuilder';
import { SourcingPage } from './pages/Sourcing/SourcingPage';
import { SourcingListsView } from './pages/Sourcing/SourcingListsView';
import { SourcingResultsPage } from './pages/Sourcing/SourcingResultsPage';
import { SourcingAgentQueryResultPage } from './pages/Sourcing/SourcingAgentQueryResultPage';
import { SourcingProfilePage } from './pages/Sourcing/SourcingProfilePage';

const KPIRequestsAndResponses = lazy(
  () => import('./pages/KPI/components/KPIRequests/KPIRequestsAndResponses')
);
const KPICompanies = lazy(() => import('./pages/KPI/components/KPICompanies/KPICompanies'));
const PortfolioOverview = lazy(() => import('./pages/PortfolioOverview/PortfolioOverview'));
const PortfolioRedirect = lazy(() =>
  import('./pages/PortfolioOverview/components/PortfolioRedirect').then((module) => ({
    default: module.PortfolioRedirect,
  }))
);
const ChatBot = lazy(() => import('./pages/ChatBot/ChatBot'));
const AdminPanel = lazy(() => import('./pages/AdminPanel/AdminPanel'));
const Finance2 = lazy(() => import('./pages/Finance2/Finance2'));
const EntityManagementPage = lazy(() =>
  import('./pages/EntityManagement/EntityManagementPage').then((module) => ({
    default: module.EntityManagementPage,
  }))
);
const StructureChartSurface = lazy(() =>
  import('./pages/EntityManagement/surfaces/PlaceholderSurfaces').then((module) => ({
    default: module.StructureChartSurface,
  }))
);
const RegistrySurface = lazy(() =>
  import('./pages/EntityManagement/surfaces/PlaceholderSurfaces').then((module) => ({
    default: module.RegistrySurface,
  }))
);
const DOSlateSurface = lazy(() =>
  import('./pages/EntityManagement/surfaces/PlaceholderSurfaces').then((module) => ({
    default: module.DOSlateSurface,
  }))
);
const BeneficialOwnershipSurface = lazy(() =>
  import('./pages/EntityManagement/surfaces/PlaceholderSurfaces').then((module) => ({
    default: module.BeneficialOwnershipSurface,
  }))
);
const ReviewQueuesSurface = lazy(() =>
  import('./pages/EntityManagement/surfaces/ReviewQueues/ReviewQueuesSurface').then((module) => ({
    default: module.ReviewQueuesSurface,
  }))
);
const DocumentHubPage = lazy(() =>
  import('./pages/DocumentHub/DocumentHubPage').then((module) => ({ default: module.DocumentHubPage }))
);
const DocumentHub = lazy(() => import('./pages/DocumentHub/DocumentHub'));
const DocumentHubPreview = lazy(() =>
  import('./pages/DocumentHub/DocumentHubPreview').then((module) => ({ default: module.DocumentHubPreview }))
);
const FinanceModulePreview = lazy(() =>
  import('./pages/Finance2/FinanceModulePreview').then((module) => ({
    default: module.FinanceModulePreview,
  }))
);
const EntityManagementModulePreview = lazy(() =>
  import('./pages/EntityManagement/EntityManagementModulePreview').then((module) => ({
    default: module.EntityManagementModulePreview,
  }))
);
const PortfolioModulePreview = lazy(() =>
  import('./pages/PortfolioOverview/PortfolioModulePreview').then((module) => ({
    default: module.PortfolioModulePreview,
  }))
);
const FundProfiles = lazy(() => import('./pages/FundProfiles/FundProfiles'));

// keep the list-origin navigation state through the summary-tab redirect
function FPIndexRedirect() {
  const location = useLocation();
  return <Navigate to={ROUTES.FP_SUMMARY} replace state={location.state} />;
}
const DealFlowPreLoader = lazy(() => import('./pages/DealFlow2/DealFlow'));
const CompanyProfiles2 = lazy(() => import('./pages/CompanyProfiles2/CompanyProfiles2'));
const IntegrationApps = lazy(() => import('./pages/Integrations/IntegrationApps'));
const CartaConfiguration = lazy(() => import('./pages/Integrations/Carta/CartaConfiguration'));
const CartaFirmsTab = lazy(() => import('./pages/Integrations/Carta/tabs/FirmsTab'));
const CartaFundsTab = lazy(() => import('./pages/Integrations/Carta/tabs/FundsTab'));
const CartaCompaniesTab = lazy(() => import('./pages/Integrations/Carta/tabs/CompaniesTab'));
const CartaHoldersTab = lazy(() => import('./pages/Integrations/Carta/tabs/HoldersTab'));
const CartaRunsTab = lazy(() => import('./pages/Integrations/Carta/tabs/RunsTab'));
const Mappings = lazy(() => import('./pages/Mappings/Mappings'));
const ReconciliationMonitor = lazy(
  () => import('./pages/AdminPanel/ReconciliationMonitor/ReconciliationMonitor')
);
const AtlasConsole = lazy(() => import('./pages/AdminPanel/AtlasConsole/AtlasConsole'));
const Processing = lazy(() => import('./pages/AdminPanel/Processing/Processing'));
const ArgusProfileInspector = lazy(
  () => import('./pages/AdminPanel/ArgusProfileInspector/ArgusProfileInspector')
);
const ArgusProfileDetail = lazy(() => import('./pages/AdminPanel/ArgusProfileInspector/ArgusProfileDetail'));

function getDataCollectionModuleRoutes(
  permSvc: PermissionService,
  usingConsolidatedDataCollection: boolean,
  useManagedServices: boolean,
  useStarbreakerCollectionV2: boolean
): JSX.Element[] {
  if (permSvc.hasPermission(PermissionKey.canReadDataCollection)) {
    const consolidatedDataCollectionRoutes: JSX.Element[] = useStarbreakerCollectionV2
      ? [
          <Route key={ROUTES.KPI} path={ROUTES.KPI} element={<KpiModuleGate variant='consolidatedV2' />}>
            <Route path={ROUTES.COMPANIES} element={<KPICompanies />}>
              <Route index element={<AllCompanies />} />
              <Route path={ROUTES.ASSIGNED_COMPANIES} element={<AssignedCompanies />} />
            </Route>
            <Route path={ROUTES.KPI_CUSTOM_TEMPLATE_CONFIGURATION}>
              <Route index element={<CustomKpiTemplateRedirect />} />
              <Route path=':templateId' element={<CustomKpiTemplatePage />} />
            </Route>
            <Route path={ROUTES.KPI_MANAGE} element={<CompanyPeriodRequestManageLoaderV2 />} />
          </Route>,
        ]
      : [
          <Route key={ROUTES.KPI} path={ROUTES.KPI} element={<KpiModuleGate variant='consolidated' />}>
            <Route path={ROUTES.COMPANIES} element={<KpiCompanySettings />} />
            <Route path={ROUTES.KPI_DATA_COLLECTION_CONFIGURATION}>
              <Route index element={<KpiCollectionConfigsRedirect />} />
              <Route path=':configId' element={<KpiCollectionConfigsPage />} />
            </Route>
            <Route path={ROUTES.KPI_MANAGE} element={<CompanyPeriodRequestManageLoader />} />
          </Route>,
        ];

    const nonConsolidatedDataCollectionRoutes: JSX.Element[] = [
      <Route key={ROUTES.KPI} path={ROUTES.KPI} element={<KpiModuleGate variant='legacy' />}>
        <Route path={ROUTES.KPI_REQUESTS} key={ROUTES.KPI_REQUESTS} element={<KPIRequestsAndResponses />}>
          <Route index element={<UnsentKpiRequestsGrid />} />
          <Route
            path={ROUTES.KPI_REQUESTS_IN_PROGRESS}
            element={<SentRequests status={KPIRequestStatus.Sent} />}
          />
          <Route
            path={ROUTES.KPI_REQUESTS_ARCHIVED}
            element={<SentRequests status={KPIRequestStatus.Archived} />}
          />
        </Route>
        <Route path={ROUTES.KPI_RESPONSES} element={<KPIResponses />}>
          <Route index element={<KPIResponsesGrid status={KPIRequestStatus.Submitted} />} />
          {useManagedServices && (
            <Route
              path={ROUTES.KPI_RESPONSES_MS_REVIEWED}
              element={<KPIResponsesGrid status={KPIRequestStatus.ManagedServiceFinished} />}
            />
          )}
          <Route path={ROUTES.KPI_RESPONSES_ACCEPTED} element={<Outlet />}>
            <Route index element={<KPIResponsesGrid status={KPIRequestStatus.Accepted} />} />
            <Route path={ROUTES.KPI_RESPONSES_ANALYSIS} element={<ResponseAnalysis />} />
          </Route>
          <Route
            path={ROUTES.KPI_RESPONSES_REJECTED}
            element={<KPIResponsesGrid status={KPIRequestStatus.Rejected} />}
          />
        </Route>

        <Route path={ROUTES.COMPANIES} element={<KPICompanies />}>
          <Route index key={ROUTES.COMPANIES} element={<AllCompanies />} />
          <Route path={ROUTES.ASSIGNED_COMPANIES} element={<AssignedCompanies />} />
        </Route>

        <Route path={ROUTES.KPI_TEMPLATES}>
          <Route index element={<KPITemplatesRedirect />} />
          <Route path=':templateId' element={<KPITemplate />} />
        </Route>

        <Route path={ROUTES.KPI_MANAGE} element={<KpiRequestManageLoader />} />
      </Route>,
    ];

    const result = usingConsolidatedDataCollection
      ? consolidatedDataCollectionRoutes
      : nonConsolidatedDataCollectionRoutes;

    if (permSvc.hasPermission(PermissionKey.canSubmitCollectionSubmit)) {
      result.push(
        <Fragment key={'data-collection-form-routes'}>
          <Route
            key={ROUTES.KPI_THANK_YOU_SCREEN}
            path={ROUTES.KPI_THANK_YOU_SCREEN}
            element={
              <Stack width='100%' height='100%'>
                <KPIThankYouScreen />
              </Stack>
            }
          />
          <Route
            key={ROUTES.KPI_PENDING_REQUESTS}
            path={ROUTES.KPI_PENDING_REQUESTS}
            element={
              <Stack width='100%' height='100%'>
                <KPIRecipientView3 />
              </Stack>
            }
          />
        </Fragment>
      );
    }

    return result;
  } else {
    return [<Route path={`${ROUTES.KPI}/*`} element={<RouteNotAllowed />} key={ROUTES.KPI} />];
  }
}

/**
 * Get Company Profile routes for users with auditor access
 * Auditors only have access to Summary and Financials tabs
 */
function getCompanyProfileAuditorRoutes(
  permSvc: PermissionService,
  showPeopleReporting: boolean
): JSX.Element[] {
  return [
    // Summary route
    permSvc.hasPermission(PermissionKey.canViewMetrics) ? (
      <Route key='summary' path={ROUTES.COMPANY_PROFILE_SUMMARY} element={<CompanySummary2 />} />
    ) : (
      <Route
        key='summary-blocked'
        path={`${ROUTES.COMPANY_PROFILE_SUMMARY}/*`}
        element={<RouteNotAllowed />}
      />
    ),

    // Financials route
    permSvc.hasPermission(PermissionKey.canViewCompanyFinancials) ? (
      <Route
        key='financials'
        path={ROUTES.COMPANY_PROFILE_FINANCIALS}
        element={<CompanyProfileFinancials />}
      />
    ) : (
      <Route
        key='financials-blocked'
        path={`${ROUTES.COMPANY_PROFILE_FINANCIALS}/*`}
        element={<RouteNotAllowed />}
      />
    ),

    // Block all other routes for auditors
    <Route
      key='performance-blocked'
      path={`${ROUTES.COMPANY_PROFILE_PERFORMANCE}/*`}
      element={<RouteNotAllowed />}
    />,
    ...(showPeopleReporting
      ? [
          <Route
            key='people-blocked'
            path={`${ROUTES.COMPANY_PROFILE_PEOPLE}/*`}
            element={<RouteNotAllowed />}
          />,
        ]
      : []),
    <Route
      key='scenarios-blocked'
      path={`${ROUTES.COMPANY_PROFILE_SCENARIOS}/*`}
      element={<RouteNotAllowed />}
    />,
    <Route
      key='captable-blocked'
      path={`${ROUTES.COMPANY_PROFILE_CAP_TABLE}/*`}
      element={<RouteNotAllowed />}
    />,
    <Route key='deals-blocked' path={`${ROUTES.DEALS}/*`} element={<RouteNotAllowed />} />,
    <Route
      key='mappings-blocked'
      path={`${ROUTES.COMPANY_PROFILE_MAPPINGS}/*`}
      element={<RouteNotAllowed />}
    />,
  ];
}

/**
 * Get standard Company Profile routes for non-auditor users
 */
function getCompanyProfileStandardRoutes(
  permSvc: PermissionService,
  showPeopleReporting: boolean,
  showDealTab: boolean,
  showWeightedScenarios: boolean
): JSX.Element[] {
  const routes: JSX.Element[] = [
    permSvc.hasPermission(PermissionKey.canViewMetrics) ? (
      <Route key='summary' path={ROUTES.COMPANY_PROFILE_SUMMARY} element={<CompanySummary2 />} />
    ) : (
      <Route
        key='summary-blocked'
        path={`${ROUTES.COMPANY_PROFILE_SUMMARY}/*`}
        element={<RouteNotAllowed />}
      />
    ),
    permSvc.hasPermission(PermissionKey.canViewKpis) ? (
      <Route
        key='performance'
        path={ROUTES.COMPANY_PROFILE_PERFORMANCE}
        element={<CompanyProfilePerformance />}
      />
    ) : (
      <Route
        key='performance-blocked'
        path={`${ROUTES.COMPANY_PROFILE_PERFORMANCE}/*`}
        element={<RouteNotAllowed />}
      />
    ),
    ...(showPeopleReporting
      ? [<Route key='people' path={ROUTES.COMPANY_PROFILE_PEOPLE} element={<CompanyProfilesPeople />} />]
      : []),
  ];

  // Add remaining routes
  routes.push(
    permSvc.hasPermission(PermissionKey.canViewCompanyFinancials) ? (
      <Route
        key='financials'
        path={ROUTES.COMPANY_PROFILE_FINANCIALS}
        element={<CompanyProfileFinancials />}
      />
    ) : (
      <Route
        key='financials-blocked'
        path={`${ROUTES.COMPANY_PROFILE_FINANCIALS}/*`}
        element={<RouteNotAllowed />}
      />
    ),
    permSvc.hasPermission(PermissionKey.canViewCaptable) ? (
      <Route key='captable' path={ROUTES.COMPANY_PROFILE_CAP_TABLE} element={<Outlet />}>
        <Route index element={<CompanyProfileCapTable />} />
        {permSvc.hasPermission(PermissionKey.canWriteCaptable) && (
          <Route path={ROUTES.CAP_TABLE_FORM} element={<CaptableFormWrapper />} />
        )}
      </Route>
    ) : (
      <Route
        key='captable-blocked'
        path={`${ROUTES.COMPANY_PROFILE_CAP_TABLE}/*`}
        element={<RouteNotAllowed />}
      />
    ),
    permSvc.hasPermission(PermissionKey.canViewScenarios) ? (
      <Route key='scenarios' path={ROUTES.COMPANY_PROFILE_SCENARIOS} element={<Outlet />}>
        <Route index element={<ScenariosWrapper />} />
        <Route path={ROUTES.COMPANY_PROFILE_SINGLE_SCENARIO} element={<SingleScenario />}>
          <Route path={ROUTES.COMPANY_PROFILE_SCENARIO_SETTINGS} element={<ScenarioForm />} />
          <Route path={ROUTES.COMPANY_PROFILE_SCENARIO_RUN} element={<ScenarioVisualization />} />
          {showWeightedScenarios && (
            <Route
              path={ROUTES.COMPANY_PROFILE_SCENARIO_WEIGHTED}
              element={<WeightedOutcomeVisualization />}
            />
          )}
        </Route>
      </Route>
    ) : (
      <Route
        key='scenarios-blocked'
        path={`${ROUTES.COMPANY_PROFILE_SCENARIOS}/*`}
        element={<RouteNotAllowed />}
      />
    ),
    showDealTab && permSvc.hasPermission(PermissionKey.canViewMetrics) ? (
      <Route
        key='deals'
        path={ROUTES.DEALS}
        element={
          <FrostedGate target='deals' preview={<DealsTabPreview />}>
            <DealsTab />
          </FrostedGate>
        }
      />
    ) : (
      <Route key='deals-blocked' path={`${ROUTES.DEALS}/*`} element={<RouteNotAllowed />} />
    ),
    permSvc.hasPermission(PermissionKey.hasMappingsModule) ? (
      <Route key='mappings' path={ROUTES.COMPANY_PROFILE_MAPPINGS} element={<Mappings />} />
    ) : (
      <Route
        key='mappings-blocked'
        path={`${ROUTES.COMPANY_PROFILE_MAPPINGS}/*`}
        element={<RouteNotAllowed />}
      />
    )
  );

  return routes;
}

function getCompanyProfileRoutes(
  permSvc: PermissionService,
  showPeopleReporting: boolean,
  showDealTab: boolean,
  showWeightedScenarios: boolean
): JSX.Element[] {
  const isAuditor = permSvc.hasPermission(PermissionKey.hasAuditorAccess);

  return [
    <Route path={ROUTES.COMPANY_PROFILES} key={ROUTES.COMPANY_PROFILES}>
      <Route path={`:id`} element={<CompanyProfiles2 />}>
        {isAuditor
          ? getCompanyProfileAuditorRoutes(permSvc, showPeopleReporting)
          : getCompanyProfileStandardRoutes(permSvc, showPeopleReporting, showDealTab, showWeightedScenarios)}
      </Route>
    </Route>,
  ];
}

export function RoutedApp() {
  const {
    showSourcing,
    showAllSecurities,
    showFundCapitalAllocation,
    showAdminManageAccess,
    showCompanyMappings,
    showFieldsManager,
    showFormattersConfig,
    showPeopleReporting,
    showDealTab,
    showFundReserves,
    showGeneralLedger,
    showGlSoiRecon,
    showMetricsRecon,
    useManagedServices,
    showAdminManageAccessColumnGridDisplay,
    useStarbreakerCollectionV2,
    showWeightedScenarios,
    showDocumentHub,
  } = useFlags<MaggieFeatureFlags>();
  const permSvc = PermissionService.get();
  const conditionalRoutes: JSX.Element[] = [];
  const isEntityManagementEnabled = useEntityManagementEnabled();
  const isKpiSourceHierarchyAvailable = useKpiSourceHierarchyAvailable();
  const isBeneficialOwnershipEnabled = useBeneficialOwnershipEnabled();
  const enableFundWaterfall = useAtomValue(clientConfigFieldsAtom)?.Allow_Fund_Waterfall ?? false;
  const usingConsolidatedDataCollection =
    (useAtomValue(clientConfigFieldsAtom)?.Using_Consolidated_Data_Collection as boolean) ?? false;
  const isCollectionOnly = useIsCollectionOnly();

  if (permSvc.hasPermission(PermissionKey.hasFundProfilesModule)) {
    conditionalRoutes.push(
      <Route key={ROUTES.FUND_PROFILES} path={`${ROUTES.FUND_PROFILES}/:fundId`} element={<FundProfiles />}>
        <Route index element={<FPIndexRedirect />} />
        <Route path={ROUTES.FP_SUMMARY} element={<FPSummary />} />
        {enableFundWaterfall && <Route path={ROUTES.FP_WATERFALL} element={<FPWaterfall />} />}
        {showFundReserves && <Route path={ROUTES.FP_RESERVES} element={<FundReservesLoader />} />}
        {showFundCapitalAllocation && (
          <Route path={ROUTES.FP_CAPITAL_ALLOCATION} element={<FPAllocationLoader />} />
        )}
      </Route>
    );
  } else {
    conditionalRoutes.push(
      <Route path={`${ROUTES.FUND_PROFILES}/*`} element={<RouteNotAllowed />} key={ROUTES.FUND_PROFILES} />
    );
  }

  if (permSvc.hasPermission(PermissionKey.canViewTransaction)) {
    conditionalRoutes.push(
      <Route
        key={ROUTES.FINANCE2}
        path={ROUTES.FINANCE2}
        element={
          <FrostedGate target='finance' preview={<FinanceModulePreview />}>
            <Finance2 />
          </FrostedGate>
        }
      >
        <Route path={ROUTES.FINANCE2_OVERVIEW} element={<Outlet />}>
          <Route index element={<AllTransactions />} />
          <Route path={ROUTES.FINANCE_DETAILS} element={<Outlet />}>
            <Route path={ROUTES.FINANCE_LEDGER} element={<LedgerGrid />} />
            <Route path={ROUTES.FINANCE_ROUNDS} element={<FinanceRounds />} />
            <Route
              key={ROUTES.FINANCE_TRANSACTIONS_3}
              path={ROUTES.FINANCE_TRANSACTIONS_3}
              element={<Outlet />}
            >
              <Route index element={<TransactionsTable3 />} />
              <Route path={ROUTES.FINANCE_TRANSACTION_HISTORY} element={<TransactionHistory />} />
            </Route>
          </Route>
        </Route>
        {showAllSecurities && (
          <Route
            path={ROUTES.FINANCE_ALL_SECURITIES}
            element={<AllSecurities />}
            key={ROUTES.FINANCE_ALL_SECURITIES}
          />
        )}
        <Route path={ROUTES.FINANCE_RECON} element={<ReconSection />} key={ROUTES.FINANCE_RECON}>
          <Route index element={<RoundReconGrid />} />
          {showMetricsRecon && (
            <Route
              path={ROUTES.FINANCE_RECON_METRICS}
              element={<TransactionReconGrid />}
              key={ROUTES.FINANCE_RECON_METRICS}
            />
          )}
        </Route>
      </Route>
    );
  } else {
    conditionalRoutes.push(
      <Route path={`${ROUTES.FINANCE2}/*`} element={<RouteNotAllowed />} key={ROUTES.FINANCE2} />
    );
  }

  if (isEntityManagementEnabled) {
    conditionalRoutes.push(
      <Route
        key={ROUTES.ENTITIES}
        path={ROUTES.ENTITIES}
        element={
          <FrostedGate target='entities' preview={<EntityManagementModulePreview />}>
            <EntityManagementPage />
          </FrostedGate>
        }
      >
        <Route index element={<Navigate to={ROUTES.ENTITIES_STRUCTURE_CHART} replace />} />
        <Route path={ROUTES.ENTITIES_STRUCTURE_CHART} element={<StructureChartSurface />} />
        <Route path={ROUTES.ENTITIES_REGISTRY} element={<RegistrySurface />} />
        <Route path={ROUTES.ENTITIES_DO_SLATE} element={<DOSlateSurface />} />
        {isBeneficialOwnershipEnabled && (
          <Route path={ROUTES.ENTITIES_BENEFICIAL_OWNERSHIP} element={<BeneficialOwnershipSurface />} />
        )}
        <Route path={ROUTES.ENTITIES_REVIEW_QUEUES} element={<ReviewQueuesSurface />} />
      </Route>
    );
  } else {
    conditionalRoutes.push(
      <Route path={`${ROUTES.ENTITIES}/*`} element={<RouteNotAllowed />} key={ROUTES.ENTITIES} />
    );
  }

  conditionalRoutes.push(
    <Route key={ROUTES.INTEGRATIONS} path={ROUTES.INTEGRATIONS} element={<Outlet />}>
      <Route path={ROUTES.INTEGRATIONS_APPS} element={<IntegrationApps />}>
        <Route path={ROUTES.INTEGRATIONS_APPS_AFFINITY} element={<AffinityConfiguration />} />
        <Route path={ROUTES.INTEGRATIONS_APPS_AIRTABLE} element={<AirtableSync />} />
        <Route path={ROUTES.INTEGRATIONS_APPS_ALLVUE} element={<AllVueConfiguration />} />
        <Route path={ROUTES.INTEGRATIONS_APPS_ARKPES} element={<ArkPesConfiguration />} />
        <Route path={ROUTES.INTEGRATIONS_APPS_ILEVEL} element={<ILevelConfiguration />} />
        <Route path={ROUTES.INTEGRATIONS_APPS_QUICKBOOKS} element={<QuickBooksConfiguration />} />
        <Route path={ROUTES.INTEGRATIONS_APPS_STANDARD_METRICS} element={<StandardMetricsConfiguration />} />
        <Route path={ROUTES.INTEGRATIONS_APPS_COBALT} element={<CobaltConfiguration />} />
        <Route path={ROUTES.INTEGRATIONS_APPS_CARTA} element={<CartaConfiguration />}>
          <Route index element={<Navigate to={ROUTES.COMPANIES} replace />} />
          <Route path={ROUTES.CARTA_FIRMS} element={<CartaFirmsTab />} />
          <Route path={ROUTES.CONFIG_FUNDS} element={<CartaFundsTab />} />
          <Route path={ROUTES.COMPANIES} element={<CartaCompaniesTab />} />
          <Route path={ROUTES.CARTA_HOLDERS} element={<CartaHoldersTab />} />
          <Route path={ROUTES.CARTA_RUNS} element={<CartaRunsTab />} />
        </Route>
        <Route path=':appName' element={<IntegrationDetail />} />
      </Route>
    </Route>
  );

  conditionalRoutes.push(
    ...getDataCollectionModuleRoutes(
      permSvc,
      usingConsolidatedDataCollection,
      useManagedServices,
      useStarbreakerCollectionV2
    )
  );
  conditionalRoutes.push(
    <Route
      key={ROUTES.BULK_UPLOAD_REPORT}
      path={`${ROUTES.BULK_UPLOAD_REPORT}/:uploadId`}
      element={<BulkUploadReportLoader />}
    />
  );

  if (PermissionService.get().hasPermission(PermissionKey.canViewAppAdminPanel)) {
    conditionalRoutes.push(
      <Route key={ROUTES.ADMIN_PANEL} path={ROUTES.ADMIN_PANEL} element={<AdminPanel />}>
        {PermissionService.get().hasPermission(PermissionKey.canViewClientSettings) && (
          <Route path={ROUTES.APPLICATION_SETTINGS} element={<ConfigClient />} />
        )}
        {showFieldsManager && <Route path={ROUTES.ADMIN_PANEL_FIELD_MANAGER} element={<FieldManager />} />}
        <Route path={ROUTES.ADMIN_PANEL_FIELDS} element={<CustomFields />} />
        <Route path={ROUTES.ADMIN_PANEL_COLUMNS} element={<ConfigColumns />} />
        {showFormattersConfig && PermissionService.get().hasPermission(PermissionKey.customerSuccessAll) && (
          <Route path={ROUTES.ADMIN_PANEL_FORMATTERS} element={<ConfigFormatters />} />
        )}
        {showCompanyMappings && <Route path={ROUTES.ADMIN_PANEL_MAPPINGS} element={<CompanyMappings />} />}
        {showAdminManageAccess && (
          <>
            <Route
              path={ROUTES.ADMIN_PANEL_MANAGE_ACCESS}
              element={
                showAdminManageAccessColumnGridDisplay ? <ManageAccess /> : <ManageAccessCompanyDataLoader />
              }
            />
            {showAdminManageAccessColumnGridDisplay && (
              <Route
                path={`${ROUTES.ADMIN_PANEL_MANAGE_ACCESS}/${ROUTES.ADMIN_PANEL_MANAGE_ACCESS_COMPANY}`}
                element={<ManageAccessCompanyDataLoader />}
              />
            )}
            {showAdminManageAccessColumnGridDisplay && (
              <Route
                path={`${ROUTES.ADMIN_PANEL_MANAGE_ACCESS}/${ROUTES.ADMIN_PANEL_MANAGE_ACCESS_COLUMN_GRID_DISPLAY}`}
                element={<ManageAccessColumnGridDisplayDataLoader />}
              />
            )}
          </>
        )}
        {PermissionService.get().hasPermission(PermissionKey.canViewClientSettings) && (
          <>
            <Route path={ROUTES.ADMIN_PANEL_DATA_COLLECTION} element={<DataCollectionConfig />} />
            <Route path={ROUTES.ADMIN_PANEL_AUDITOR_CONFIG} element={<AuditorDateRangesConfig />} />
            <Route path={ROUTES.ADMIN_PANEL_ROUND_CREATION} element={<RoundCreationConfig />} />
          </>
        )}
        <Route path={ROUTES.ADMIN_PANEL_DATA_DISPLAY} element={<DataDisplay />}>
          <Route path={ROUTES.COMPANY_LIST} element={<ListCustomization />} />
          <Route
            path={`${ROUTES.COMPANY_LIST}/${ROUTES.COMPANY_LIST_OVERVIEW}`}
            element={<CompanyListsOverviewConfig />}
          />
          <Route path={ROUTES.COMPANY_PROFILES} element={<ProfileCustomization />} />
          <Route
            path={`${ROUTES.COMPANY_PROFILES}/${ROUTES.DETAILS_CONFIG}`}
            element={<CompanyDetailsConfig />}
          />
          <Route
            path={`${ROUTES.COMPANY_PROFILES}/${ROUTES.METRICS_CONFIG}`}
            element={<InvestmentMetricsConfig />}
          />
          <Route
            path={`${ROUTES.COMPANY_PROFILES}/${ROUTES.COMPANY_SUMMARY_FINANCIAL_SETTINGS}`}
            element={<CompanySummaryFinancialSettingsConfig />}
          />
          <Route
            path={`${ROUTES.COMPANY_PROFILES}/${ROUTES.COMPANY_SUMMARY_PERFORMANCE_SETTINGS}`}
            element={<CompanySummaryPerformanceSettingsConfig />}
          />
          <Route
            path={`${ROUTES.COMPANY_PROFILES}/${ROUTES.COMPANY_SUMMARY_KPI_CHARTS}`}
            element={<CompanySummaryKPIChartsConfig />}
          />
          <Route path={ROUTES.KPI_DISPLAY} element={<KpiDisplayConfig />} />
          <Route path={ROUTES.TEAR_SHEETS} element={<TearSheetConfig />} />
          <Route
            path={`${ROUTES.TEAR_SHEETS}/${ROUTES.TEAR_SHEETS_HEADER_CONFIG}`}
            element={<TearSheetHeaderConfig />}
          />
          <Route
            path={`${ROUTES.TEAR_SHEETS}/${ROUTES.TEAR_SHEETS_COMPANY_SUMMARY_CONFIG}`}
            element={<TearSheetCompanySummaryConfig />}
          />
          <Route
            path={`${ROUTES.TEAR_SHEETS}/${ROUTES.TEAR_SHEETS_INVESTMENT_SUMMARY_CONFIG}`}
            element={<TearSheetInvestmentSummaryConfig />}
          />
          <Route
            path={`${ROUTES.TEAR_SHEETS}/${ROUTES.TEAR_SHEETS_PERFORMANCE_SUMMARY_CONFIG}`}
            element={<TearSheetPerformanceSummaryConfig />}
          />
          <Route
            path={`${ROUTES.TEAR_SHEETS}/${ROUTES.TEAR_SHEETS_CAPITALIZATION_SUMMARY_CONFIG}`}
            element={<TearSheetCapitalizationSummaryConfig />}
          />
          <Route
            path={`${ROUTES.TEAR_SHEETS}/${ROUTES.TEAR_SHEETS_FINANCING_HISTORY_CONFIG}`}
            element={<TearSheetFinancingHistoryConfig />}
          />
          <Route
            path={`${ROUTES.TEAR_SHEETS}/${ROUTES.TEAR_SHEETS_INVESTMENT_THESIS_CONFIG}`}
            element={<TearSheetInvestmentThesisConfig />}
          />
          <Route
            path={`${ROUTES.TEAR_SHEETS}/${ROUTES.TEAR_SHEETS_PERFORMANCE_COMMENTARY_CONFIG}`}
            element={<TearSheetPerformanceCommentaryConfig />}
          />
          <Route
            path={`${ROUTES.TEAR_SHEETS}/${ROUTES.TEAR_SHEETS_COMPANY_DESCRIPTION_CONFIG}`}
            element={<TearSheetCompanyDescriptionConfig />}
          />
          <Route path={ROUTES.TEAR_SHEETS_V1} element={<TearsheetV1LayoutBuilder />} />
          <Route path={ROUTES.TEAR_SHEETS_V2} element={<TearsheetV2LayoutBuilder />} />
        </Route>
        <Route
          path={ROUTES.ADMIN_PANEL_ORG_PROFILE}
          element={<OrgProfile />}
          key={ROUTES.ADMIN_PANEL_ORG_PROFILE}
        />
        <Route
          path={ROUTES.ADMIN_PANEL_RUBRICS}
          element={<RubricsConfigLoader />}
          key={ROUTES.ADMIN_PANEL_RUBRICS}
        />
        <Route
          path={ROUTES.ADMIN_PANEL_TEARSHEET_GENERATION}
          element={<TearsheetGeneration />}
          key={ROUTES.ADMIN_PANEL_TEARSHEET_GENERATION}
        />
        {permSvc.hasPermission(PermissionKey.canBulkExportData) && (
          <Route
            path={ROUTES.ADMIN_PANEL_TABLE_DOWNLOAD}
            element={<TableDataDownloadLoader />}
            key={ROUTES.ADMIN_PANEL_TABLE_DOWNLOAD}
          />
        )}
        <Route path={ROUTES.CONFIG} key={ROUTES.CONFIG}>
          <Route path={ROUTES.CONFIG_DEAL_TYPES} element={<ConfigDealType />} index />
          <Route path={ROUTES.CONFIG_DEAL_STAGES} element={<ConfigDealStage />} />
          <Route path={ROUTES.CONFIG_PASS_REASONS} element={<ConfigPassReasons />} />
          <Route path={ROUTES.CONFIG_MISS_REASONS} element={<ConfigMissReasons />} />
          <Route path={ROUTES.CONFIG_SECTORS} element={<ConfigSector />} />
          {permSvc.hasPermission(PermissionKey.customerSuccessAll) && (
            <Route path={ROUTES.CONFIG_FIELD_AUTOMATION} element={<FieldAutomationConfig />} />
          )}
          <Route path={ROUTES.CONFIG_FUNDS} element={<ConfigFund />} />
          <Route path={ROUTES.CONFIG_KPI} element={<ConfigKPIs />} key={ROUTES.CONFIG_KPI} />
          <Route
            path={ROUTES.CONFIG_KPI_GROUPS}
            element={<ConfigKPIGroups />}
            key={ROUTES.CONFIG_KPI_GROUPS}
          />
          {isKpiSourceHierarchyAvailable && (
            <Route
              path={ROUTES.CONFIG_KPI_SOURCES}
              element={<KpiSourcesConfig />}
              key={ROUTES.CONFIG_KPI_SOURCES}
            />
          )}
          {showGeneralLedger && (
            <Route
              path={ROUTES.GENERAL_LEDGER}
              element={<GeneralLedgerLoader />}
              key={ROUTES.GENERAL_LEDGER}
            />
          )}
          {permSvc.hasPermission(PermissionKey.customerSuccessAll) && (
            <Route
              path={ROUTES.BULK_DEAL_DOCS}
              element={<BulkDealDocsUploadLoader />}
              key={ROUTES.BULK_DEAL_DOCS}
            />
          )}
          {permSvc.hasPermission(PermissionKey.customerSuccessAll) && (
            <Route
              path={ROUTES.UPDATE_DEAL_DOCS_ROUND}
              element={<UpdateDealDocsRoundLoader />}
              key={ROUTES.UPDATE_DEAL_DOCS_ROUND}
            />
          )}
          {permSvc.hasPermission(PermissionKey.customerSuccessAll) && (
            <Route
              path={ROUTES.RESET_REINGESTION}
              element={<ResetReingestion />}
              key={ROUTES.RESET_REINGESTION}
            />
          )}
          <Route path={ROUTES.UPLOAD_STATUS} element={<UploadStatus />} key={ROUTES.UPLOAD_STATUS} />
          {showGlSoiRecon && (
            <Route
              path={ROUTES.ADMIN_PANEL_RECONCILIATION_MONITOR}
              element={<ReconciliationMonitor />}
              key={ROUTES.ADMIN_PANEL_RECONCILIATION_MONITOR}
            />
          )}
          {showGlSoiRecon && (
            <Route
              path={ROUTES.ADMIN_PANEL_ARGUS_PROFILES}
              element={<ArgusProfileInspector />}
              key={ROUTES.ADMIN_PANEL_ARGUS_PROFILES}
            />
          )}
          {showGlSoiRecon && (
            <Route
              path={ROUTES.ADMIN_PANEL_ARGUS_PROFILE_DETAIL}
              element={<ArgusProfileDetail />}
              key={ROUTES.ADMIN_PANEL_ARGUS_PROFILE_DETAIL}
            />
          )}
          {permSvc.hasPermission(PermissionKey.customerSuccessAll) && (
            <Route
              path={ROUTES.ADMIN_PANEL_ATLAS_CONSOLE}
              element={<AtlasConsole />}
              key={ROUTES.ADMIN_PANEL_ATLAS_CONSOLE}
            />
          )}
          {permSvc.hasPermission(PermissionKey.customerSuccessAll) && (
            <Route path={ROUTES.PROCESSING} element={<Processing />} key={ROUTES.PROCESSING} />
          )}
        </Route>
      </Route>
    );
  } else {
    conditionalRoutes.push(
      <Route path={`${ROUTES.ADMIN_PANEL}/*`} element={<RouteNotAllowed />} key={ROUTES.ADMIN_PANEL} />
    );
  }

  if (permSvc.hasPermission(PermissionKey.canViewList)) {
    conditionalRoutes.push(
      <Route key={ROUTES.COMPANIES} path={ROUTES.COMPANIES} element={<Outlet />}>
        <Route index element={<Navigate to={ROUTES.LISTS} replace />} />
        <Route path={ROUTES.LISTS} element={<CompanyListLoader />}>
          <Route path={`:listId`} element={<Companies />} />
        </Route>
      </Route>
    );
  } else {
    conditionalRoutes.push(
      <Route path={`${ROUTES.COMPANIES}/*`} element={<RouteNotAllowed />} key={ROUTES.COMPANIES} />
    );
  }

  if (showSourcing) {
    conditionalRoutes.push(
      <Route
        key={ROUTES.SOURCING}
        path={ROUTES.SOURCING}
        element={
          <FrostedGate target='sourcing' preview={<SourcingModulePreview />}>
            <SourcingPage />
          </FrostedGate>
        }
      >
        <Route index element={<SourcingListsView />} />
        <Route path={`${ROUTES.LISTS}/:listId`} element={<SourcingResultsPage />} />
        <Route path={`${ROUTES.SAVED_SEARCHES}/:savedSearchId`} element={<SourcingResultsPage />} />
        <Route path={`${ROUTES.SOURCING_RESULTS}/:searchId`} element={<SourcingAgentQueryResultPage />} />
        <Route path={`${ROUTES.SOURCING_PROFILE}/:companyId`} element={<SourcingProfilePage />} />
      </Route>
    );
  }

  // Doc Hub delivery needs the company to exist in Starbreaker, which only
  // consolidated data collection syncs.
  if (showDocumentHub && usingConsolidatedDataCollection) {
    conditionalRoutes.push(
      <Route
        key={ROUTES.DOCS}
        path={ROUTES.DOCS}
        element={
          <FrostedGate target='docs' preview={<DocumentHubPreview />}>
            <DocumentHubPage />
          </FrostedGate>
        }
      >
        <Route index element={<DocumentHub />} />
      </Route>
    );
  }

  const showSeparateMprRoute = getShowSeparateMprRoute();
  if (showSeparateMprRoute) {
    conditionalRoutes.push(
      <Route key={ROUTES.MULTIPERIOD_REPORTING} path={ROUTES.MULTIPERIOD_REPORTING} element={<Outlet />}>
        <Route path={`:viewId`} element={<TempMprWrapper />} />
      </Route>
    );
  } else {
    conditionalRoutes.push(
      <Route
        path={`${ROUTES.MULTIPERIOD_REPORTING}/*`}
        element={<RouteNotAllowed />}
        key={ROUTES.MULTIPERIOD_REPORTING}
      />
    );
  }

  if (permSvc.hasPermission(PermissionKey.canViewMetrics)) {
    conditionalRoutes.push(
      <Route
        key={`${ROUTES.PORTFOLIO}/:viewId`}
        path={`${ROUTES.PORTFOLIO}/:viewId`}
        element={
          <FrostedGate target='portfolio' preview={<PortfolioModulePreview />}>
            <PortfolioOverview />
          </FrostedGate>
        }
      />
    );
    // Base portfolio route - redirects to first available view based on user permissions
    conditionalRoutes.push(
      <Route
        key={ROUTES.PORTFOLIO}
        path={ROUTES.PORTFOLIO}
        element={
          <FrostedGate target='portfolio' preview={<PortfolioModulePreview />}>
            <PortfolioRedirect />
          </FrostedGate>
        }
      />
    );
  } else {
    conditionalRoutes.push(
      <Route path={`${ROUTES.PORTFOLIO}/*`} element={<RouteNotAllowed />} key={ROUTES.PORTFOLIO} />
    );
  }

  if (permSvc.hasPermission(PermissionKey.canReadDataCollection)) {
    conditionalRoutes.push(
      <Route key={ROUTES.KPI_REQUEST_REVIEW} path={ROUTES.KPI_REQUEST_REVIEW} element={<KPIReviewerView />} />
    );
  } else {
    conditionalRoutes.push(
      <Route
        path={`${ROUTES.KPI_REQUEST_REVIEW}/*`}
        element={<RouteNotAllowed />}
        key={ROUTES.KPI_REQUEST_REVIEW}
      />
    );
  }

  if (permSvc.hasPermission(PermissionKey.canViewDeal)) {
    conditionalRoutes.push(
      <Route
        key={ROUTES.DEAL_FLOW}
        path={ROUTES.DEAL_FLOW}
        element={
          <FrostedGate target='pipeline' preview={<PipelineModulePreview />}>
            <Outlet />
          </FrostedGate>
        }
      >
        <Route path={ROUTES.DEAL_ANALYTICS} element={<DealAnalytics />}>
          <Route path={ROUTES.DEAL_ANALYTICS_KPI} element={<DAOverview />} />
          <Route path={ROUTES.DEAL_ANALYTICS_DURATION_DETAILS} element={<DealAnalyticsDuration />} />
        </Route>

        <Route path={`${ROUTES.DEALS}?`} element={<DealFlowPreLoader />}>
          <Route path={`:dealCategory`} element={<DealsView />} />
        </Route>
      </Route>
    );
  } else {
    conditionalRoutes.push(
      <Route path={`${ROUTES.DEAL_FLOW}/*`} element={<RouteNotAllowed />} key={ROUTES.DEAL_FLOW} />
    );
  }

  if (!permSvc.hasPermission(PermissionKey.baseline)) {
    return (
      <Routes>
        <Route path='*' element={<RouteNotAllowed />} />
      </Routes>
    );
  }

  if (PermissionService.get().hasPersona(PersonaKey.respondent)) {
    return (
      <Routes>
        <Route path={'/'} element={<RouteWrapper />}>
          <Route
            key={ROUTES.KPI_THANK_YOU_SCREEN}
            path={ROUTES.KPI_THANK_YOU_SCREEN}
            element={<KPIThankYouScreen />}
          />
          <Route
            key={ROUTES.KPI_PENDING_REQUESTS}
            path={ROUTES.KPI_PENDING_REQUESTS}
            element={<KPIRecipientView3 />}
          />
          <Route path='*' element={<RouteNotAllowed />} />
        </Route>
      </Routes>
    );
  }

  if (permSvc.hasPersona(PersonaKey.managedServices)) {
    if (!permSvc.hasPermission(PermissionKey.canReadDataCollection)) {
      return (
        <Routes>
          <Route path='*' element={<RouteNotAllowed />} />
        </Routes>
      );
    }
    return (
      <Routes>
        <Route path={'/'} element={<RouteWrapper />}>
          <Route key={ROUTES.KPI} path={ROUTES.KPI} element={<KpiModuleGate variant='managedServices' />}>
            <Route path={ROUTES.KPI_RESPONSES} element={<KPIResponses />}>
              <Route index element={<Navigate to={ROUTES.KPI_RESPONSES_MS_PENDING} replace />} />
              <Route
                path={ROUTES.KPI_RESPONSES_MS_PENDING}
                element={<KPIResponsesGrid status={KPIRequestStatus.ManagedServiceSubmitted} />}
              />
              <Route
                path={ROUTES.KPI_RESPONSES_MS_IN_PROGRESS}
                element={<KPIResponsesGrid status={KPIRequestStatus.ManagedServiceInProgress} />}
              />
              <Route
                path={ROUTES.KPI_RESPONSES_MS_REVIEWED}
                element={<KPIResponsesGrid status={KPIRequestStatus.ManagedServiceFinished} />}
              />
              <Route
                path={ROUTES.KPI_RESPONSES_REJECTED}
                element={<KPIResponsesGrid status={KPIRequestStatus.Rejected} />}
              />
            </Route>
          </Route>
          <Route
            key={ROUTES.KPI_REQUESTS_RESPONSES_HOME}
            path={ROUTES.KPI_REQUESTS_RESPONSES_HOME}
            element={<KPIRequestResponseHome />}
          />
          <Route
            key={ROUTES.KPI_REQUEST_REVIEW}
            path={ROUTES.KPI_REQUEST_REVIEW}
            element={<ManagedServiceView />}
          />
          <Route path='*' element={<RouteNotAllowed />} />
        </Route>
      </Routes>
    );
  }

  if (isCollectionOnly) {
    return (
      <Routes>
        <Route path={'/signout'} element={<Navigate replace to='/' />} />
        <Route path={'/'} element={<RouteWrapper />}>
          <Route index element={<Navigate to={ROUTES.KPI} replace />} />
          <Route path={ROUTES.CHATBOT} element={<ChatBot />} />

          {/* Data Collection Module */}
          {getDataCollectionModuleRoutes(
            permSvc,
            usingConsolidatedDataCollection,
            useManagedServices,
            useStarbreakerCollectionV2
          )}
          {permSvc.hasPermission(PermissionKey.canReadDataCollection) && (
            <>
              <Route
                key={ROUTES.KPI_REQUESTS_RESPONSES_HOME}
                path={ROUTES.KPI_REQUESTS_RESPONSES_HOME}
                element={<KPIRequestResponseHome />}
              />
              <Route
                key={ROUTES.KPI_REQUEST_REVIEW}
                path={ROUTES.KPI_REQUEST_REVIEW}
                element={<KPIReviewerView />}
              />
            </>
          )}

          {/* Company Profiles */}
          {getCompanyProfileRoutes(permSvc, showPeopleReporting, showDealTab, showWeightedScenarios)}

          <Route path='*' element={<CollectionOnlyRouteNotAllowed />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path={'/signout'} element={<Navigate replace to='/' />} />

      <Route path={'/'} element={<RouteWrapper />}>
        <Route index element={<IndexComponent />} />
        <Route path={ROUTES.CHATBOT} element={<ChatBot />} />
        <Route path={ROUTES.EXCEL_LOGIN} element={<LoginSuccess />} />
        <Route path={ROUTES.COMPANIES_OVERVIEW} element={<CompaniesOverview />} />
        {isNotProduction() && <Route path={ROUTES.UI} element={<UI />} />}
        {isNotProduction() && <Route path={'cap'} element={<UI />} />}

        {getCompanyProfileRoutes(permSvc, showPeopleReporting, showDealTab, showWeightedScenarios)}
        {conditionalRoutes}
        <Route path='*' element={<Navigate to='/' />} key='*' />
      </Route>
    </Routes>
  );
}

function IndexComponent() {
  const permSvc = PermissionService.get();
  const isCollectionOnly = useIsCollectionOnly();

  if (isMobileDevice(true)) {
    return <Navigate to={ROUTES.CHATBOT} replace />;
  }

  if (isCollectionOnly) {
    return <Navigate to={ROUTES.KPI} replace />;
  }

  if (permSvc.hasPersona(PersonaKey.limitedProfile)) {
    return <Navigate to={ROUTES.COMPANIES_OVERVIEW} replace />;
  }

  if (permSvc.hasPermission(PermissionKey.canViewMetrics)) {
    return <Navigate to={ROUTES.PORTFOLIO} replace />;
  }
  if (permSvc.hasPermission(PermissionKey.canViewList)) {
    return <Companies />;
  }
  return <div />;
}

function RouteNotAllowed() {
  return (
    <ForbiddenFallback
      error={new Error(`Forbidden: missing permissions; route: ${window.location.pathname};`)}
    />
  );
}

function CollectionOnlyRouteNotAllowed() {
  return (
    <ForbiddenError
      error={new Error(`Collection-only access restriction; route: ${window.location.pathname};`)}
      config={CollectionOnlyForbiddenMessage}
    />
  );
}
